import {
    NestedPersonnelClub,
    NestedPersonnelEvent,
    NestedPersonnelUmpire,
} from "../types/personnel";

type AffiliationLike =
    | {
          name?: string | null;
          level?: string | null;
          orgAbbreviation?: string | null;
          abbreviation?: string | null;
          teamId?: number | null;
      }
    | null
    | undefined;

type PersonnelDisplaySource = {
    club?: NestedPersonnelClub | null;
    clubStatsId?: number | null;
    affiliation?: AffiliationLike;
    event?: NestedPersonnelEvent | null;
    eventId?: number | null;
    umpire?: NestedPersonnelUmpire | null;
    umpireStatsId?: number | null;
    orgName?: string | null;
    personnelType?: { type?: string | null } | null;
};

const isClubPersonnel = (personnel: PersonnelDisplaySource): boolean => {
    const type = personnel?.personnelType?.type;
    return type != null && type.toUpperCase() === "CLUB";
};

const isUmpirePersonnel = (personnel: PersonnelDisplaySource): boolean => {
    const type = personnel?.personnelType?.type;
    return type != null && type.toUpperCase() === "UMPIRE";
};

const isVmsPersonnel = (personnel: PersonnelDisplaySource): boolean => {
    const type = personnel?.personnelType?.type;
    return type != null && type.toUpperCase() === "VMS";
};

/** Prefer nested club when present, clubStatsId linked, or personnel is CLUB; else event (VMS) or affiliation. */
export const usesClubDisplay = (personnel: PersonnelDisplaySource): boolean => {
    if (personnel?.club != null) {
        return true;
    }
    if (personnel?.clubStatsId != null) {
        return true;
    }
    if (personnel?.club?.statsTeamId != null) {
        return true;
    }
    return isClubPersonnel(personnel);
};

export const usesUmpireDisplay = (personnel: PersonnelDisplaySource): boolean => {
    if (usesClubDisplay(personnel)) {
        return false;
    }
    if (personnel?.umpire != null || personnel?.umpireStatsId != null) {
        return true;
    }
    return isUmpirePersonnel(personnel);
};

export const usesEventDisplay = (personnel: PersonnelDisplaySource): boolean => {
    if (usesClubDisplay(personnel) || usesUmpireDisplay(personnel)) {
        return false;
    }
    return personnel?.event != null || personnel?.eventId != null || isVmsPersonnel(personnel);
};

export const getOrgAbbreviation = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.orgAbbreviation ?? personnel.club?.abbreviation ?? "";
    }
    if (usesUmpireDisplay(personnel)) {
        return "";
    }
    if (usesEventDisplay(personnel)) {
        if (personnel.event != null) {
            return personnel.event.orgAbbreviation ?? personnel.event.abbreviation ?? "";
        }
        return "";
    }
    return personnel.affiliation?.orgAbbreviation ?? personnel.affiliation?.abbreviation ?? "";
};

export const getOrgOrClubName = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.name ?? "";
    }
    if (usesUmpireDisplay(personnel)) {
        return personnel.umpire?.name ?? personnel.orgName ?? "";
    }
    if (usesEventDisplay(personnel)) {
        return personnel.event?.name ?? "";
    }
    return personnel.affiliation?.name ?? "";
};

export const getOrgOrClubLevel = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.level ?? "";
    }
    if (usesUmpireDisplay(personnel)) {
        return personnel.umpire?.level ?? "";
    }
    if (usesEventDisplay(personnel)) {
        return personnel.event?.level ?? "";
    }
    return personnel.affiliation?.level ?? "";
};

/** Display label like "Yankees (Major)" / "Somerset (AAA)". */
export const buildAffiliateNameLevel = (personnel: PersonnelDisplaySource): string => {
    const name = getOrgOrClubName(personnel);
    if (!name) {
        return "";
    }
    const level = getOrgOrClubLevel(personnel);
    if (!level) {
        return name;
    }
    return `${name} (${level.trim()})`;
};

/** Header label: "NYY - Yankees". */
export const buildOrgHeaderLabel = (personnel: PersonnelDisplaySource): string => {
    const abbrev = getOrgAbbreviation(personnel);
    const name = getOrgOrClubName(personnel);
    if (abbrev && name) {
        return `${abbrev} - ${name}`;
    }
    return abbrev || name || "";
};

export const resolveClubStatsId = (personnel: PersonnelDisplaySource): number | null => {
    if (personnel?.clubStatsId != null) {
        return personnel.clubStatsId;
    }
    if (personnel?.club?.statsTeamId != null) {
        return personnel.club.statsTeamId;
    }
    return null;
};

export const resolveUmpireStatsId = (personnel: PersonnelDisplaySource): number | null => {
    if (personnel?.umpireStatsId != null) {
        return personnel.umpireStatsId;
    }
    if (personnel?.umpire?.statsapiPersonId != null) {
        return personnel.umpire.statsapiPersonId;
    }
    if (personnel?.umpire?.umpireStatsId != null) {
        return personnel.umpire.umpireStatsId;
    }
    return null;
};
